import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

// Dismissible heads-up banner shown to every logged-in user while an admin's
// deployment-notice window is active (opened when the admin publishes a new
// version). Polls /deploy-notice; the window closes on its own at restart_at.
export default function DeployNoticeBanner() {
  const { user } = useAuth();
  const [dismissedFor, setDismissedFor] = useState(null);

  const { data } = useQuery({
    queryKey: ["deploy-notice"],
    queryFn: () => api("/deploy-notice"),
    enabled: !!user,
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
  });

  const active = !!data?.active;
  const restartAt = data?.restart_at || null;

  // Live countdown so the banner reflects the time remaining each second.
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!active || !restartAt) return;
    const tick = () => setRemaining(Math.max(0, Math.round((new Date(restartAt) - Date.now()) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [active, restartAt]);

  if (!user || !active || dismissedFor === restartAt || remaining <= 0) return null;

  return (
    <div
      data-testid="deploy-notice-banner"
      className="fixed inset-x-0 top-0 z-[350] flex justify-center px-3 pt-3 pointer-events-none"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}
    >
      <div className="pointer-events-auto w-full max-w-xl bg-[#181427] text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 animate-rise">
        <div className="w-9 h-9 rounded-full bg-[#E8614A]/20 flex items-center justify-center shrink-0">
          <RefreshCw className="w-4 h-4 text-[#E8614A]" />
        </div>
        <p className="flex-1 min-w-0 text-sm leading-snug" data-testid="deploy-notice-message">
          {data?.message || "A new update is being applied. You may be logged out shortly."}
          <span className="text-white/60"> ({remaining}s)</span>
        </p>
        <button
          data-testid="deploy-notice-dismiss"
          onClick={() => setDismissedFor(restartAt)}
          aria-label="Dismiss"
          className="shrink-0 text-white/50 hover:text-white p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
